// useSurvey: survey-state van één project.
//
// - Waarden staan direct in localStorage (overleeft refresh en offline).
// - Elke gewijzigde sectie wordt als "dirty" gemarkeerd; die lijst staat óók
//   in localStorage, zodat niet-verzonden wijzigingen na een herlaad alsnog
//   naar de server gaan.
// - Bij laden wint de server, behalve voor dirty secties: daar wint lokaal.
// - Verzenden gebeurt debounced, één flush tegelijk, met automatische retry.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from '@rvdh/react-hooks';
import { api, endpoints } from '../api/client.js';
import { parseVariant, variantKey } from '../variants.js';

const DEBOUNCE_MS = 600;
const RETRY_INTERVAL_MS = 5000;
// Deze typen gaan niet via /surveys: uploads lopen via /photos, computed is afgeleid.
const NON_SURVEY_TYPES = new Set(['photo', 'file', 'computed']);

const keyOf = (clusterId, sectionId) => `${clusterId}::${sectionId}`;

/** Default waarde voor een item afhankelijk van type. */
function defaultForType(item) {
    if (item?.default !== undefined) { return item.default; }
    switch (item?.type) {
        case 'boolean': return null;
        case 'select': return item.multiselect ? [] : '';
        case 'photo':
        case 'file': return null;
        case 'dimensions': return { l: '', w: '', h: '' };
        case 'range': return { min: '', max: '' };
        case 'contact': return { name: '', phone: '', email: '' };
        default: return '';
    }
}

const present = (v) => v !== '' && v !== null && v !== undefined;

/** Is deze itemwaarde als 'ingevuld' te beschouwen? */
export function isItemFilled(item, value) {
    if (!item) { return false; }
    if (item.type === 'computed') { return true; }
    if (value === null || value === undefined) { return false; }
    switch (item.type) {
        case 'boolean':
            return typeof value === 'boolean';
        case 'select':
            if (item.multiselect) { return Array.isArray(value) && value.length > 0; }
            return typeof value === 'string' && value.trim() !== '';
        case 'number':
            return value !== '' && !Number.isNaN(Number(value));
        case 'photo':
            return Array.isArray(value) ? value.length > 0 : Boolean(value);
        case 'file':
            return typeof value === 'object' && value.status !== 'local';
        case 'dimensions':
            return present(value.l) && present(value.w) && present(value.h);
        case 'range':
            return present(value.min) && present(value.max);
        case 'contact':
            return (value.name || '').trim() !== '';
        default:
            return typeof value === 'string' ? value.trim() !== '' : Boolean(value);
    }
}

/** Zijn alle required-items van sectie gevuld? */
export function sectionComplete(section, sectionData) {
    if (!section?.items) { return true; }
    return section.items
        .filter(it => it.required)
        .every(it => isItemFilled(it, sectionData?.[it.id]));
}

/** Compleet-status cluster: alle secties compleet? Bij opties geldt de opgegeven optie. */
export function clusterComplete(cluster, clusterData, variant = 1) {
    if (!cluster?.sections) { return true; }
    return cluster.sections.every(sec => sectionComplete(sec, clusterData?.[variantKey(sec.id, variant)]));
}

/** Hoeveel items zijn ingevuld (voor progress-bar). Bij opties geldt de opgegeven optie. */
export function clusterProgress(cluster, clusterData, variant = 1) {
    if (!cluster?.sections) { return { filled: 0, total: 0 }; }
    let total = 0;
    let filled = 0;
    for (const sec of cluster.sections) {
        for (const it of sec.items || []) {
            if (it.type === 'computed') { continue; }
            total += 1;
            if (isItemFilled(it, clusterData?.[variantKey(sec.id, variant)]?.[it.id])) { filled += 1; }
        }
    }
    return { filled, total };
}

function readDirty(storageKey) {
    try {
        const raw = window.localStorage.getItem(storageKey);
        const arr = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
}

function writeDirty(storageKey, set) {
    try { window.localStorage.setItem(storageKey, JSON.stringify([...set])); } catch { /* quota */ }
}

/**
 * useSurvey(projectId, clustersConfig)
 *
 * Returns: data, setItem, setSection, getItem, getSection, syncState
 * ('idle' | 'saving' | 'error'), lastError, pendingCount, refresh, flush,
 * isReady, progress.
 */
export function useSurvey(projectId, clustersConfig, preferred = {}) {
    const storageKey = `zbs:survey:${projectId || 'none'}`;
    const dirtyKey = `zbs:survey:dirty:${projectId || 'none'}`;
    const [data, setData] = useLocalStorage(storageKey, {});
    const [syncState, setSyncState] = useState('idle');
    const [lastError, setLastError] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const [isReady, setIsReady] = useState(false);

    const dataRef = useRef(data);
    dataRef.current = data;
    const configRef = useRef(clustersConfig);
    configRef.current = clustersConfig;

    const dirtyRef = useRef(null);
    if (dirtyRef.current === null) { dirtyRef.current = readDirty(dirtyKey); }
    const versionsRef = useRef(new Map());
    const timerRef = useRef(null);
    const flushingRef = useRef(null);
    const rerunRef = useRef(false);

    const persistDirty = useCallback(() => {
        writeDirty(dirtyKey, dirtyRef.current);
        setPendingCount(dirtyRef.current.size);
    }, [dirtyKey]);

    // `section` mag een optie-achtervoegsel hebben ("E1@2"); de configuratie kent alleen "E1".
    const specSection = (cluster, section) => (configRef.current?.clusters || [])
        .find(c => c.id === cluster)?.sections?.find(s => s.id === parseVariant(section).base);

    /** Alleen de waarden die via /surveys opgeslagen worden. */
    const sanitize = useCallback((cluster, section, raw) => {
        if (!configRef.current?.clusters) { return null; } // config nog niet geladen
        const spec = specSection(cluster, section);
        // Sectie bestaat niet meer (bijv. cluster G, verwijderd 15 oktober 2026): niets te verzenden.
        if (!spec) { return {}; }
        const items = {};
        for (const item of spec.items || []) {
            if (NON_SURVEY_TYPES.has(item.type)) { continue; }
            if (!Object.prototype.hasOwnProperty.call(raw || {}, item.id)) { continue; }
            items[item.id] = raw[item.id];
        }
        return items;
    }, []);

    /** Verwijdert lokale secties van een cluster (data + openstaande verzending). */
    const dropSections = useCallback((clusterId, match) => {
        const prev = dataRef.current || {};
        const kept = {};
        for (const [key, value] of Object.entries(prev[clusterId] || {})) {
            if (match(key)) { dirtyRef.current.delete(keyOf(clusterId, key)); }
            else { kept[key] = value; }
        }
        const next = { ...prev, [clusterId]: kept };
        dataRef.current = next;
        setData(next);
        writeDirty(dirtyKey, dirtyRef.current);
        setPendingCount(dirtyRef.current.size);
    }, [setData, dirtyKey]);

    /** Na het verwijderen van een optie: lokale gegevens van die optie weg. */
    const dropVariant = useCallback((clusterId, variant) => {
        if (variant > 1) { dropSections(clusterId, (key) => parseVariant(key).variant === variant); }
    }, [dropSections]);

    const flushOnce = useCallback(async () => {
        let failed = null;
        for (const key of [...dirtyRef.current]) {
            const [cluster, section] = key.split('::');
            const version = versionsRef.current.get(key) || 0;
            const items = sanitize(cluster, section, dataRef.current?.[cluster]?.[section]);
            if (items === null) { failed = failed || new Error('configuratie nog niet geladen'); continue; }
            const done = () => {
                // Tussentijds opnieuw gewijzigd? Dan blijft de sectie dirty.
                if ((versionsRef.current.get(key) || 0) === version) { dirtyRef.current.delete(key); }
            };
            if (Object.keys(items).length === 0) { done(); continue; }
            try {
                await api.post(endpoints.survey(projectId, cluster, section), { items });
                done();
            } catch (err) {
                if (err?.statusCode === 404 && parseVariant(section).variant > 1) {
                    // De optie bestaat niet meer (verwijderd, mogelijk op een ander
                    // apparaat): lokale resten opruimen in plaats van blijven proberen.
                    dropSections(cluster, (key) => key === section);
                } else {
                    failed = err;
                }
            }
        }
        persistDirty();
        return failed;
    }, [projectId, sanitize, persistDirty, dropSections]);

    const flush = useCallback(() => {
        if (!projectId) { return Promise.resolve(); }
        if (flushingRef.current) {
            rerunRef.current = true;
            return flushingRef.current;
        }
        if (dirtyRef.current.size === 0) { return Promise.resolve(); }
        setSyncState('saving');
        flushingRef.current = (async () => {
            let failed = null;
            do {
                rerunRef.current = false;
                failed = await flushOnce();
            } while (rerunRef.current && !failed);
            setLastError(failed);
            setSyncState(failed ? 'error' : 'idle');
        })().finally(() => { flushingRef.current = null; });
        return flushingRef.current;
    }, [projectId, flushOnce]);

    const scheduleFlush = useCallback(() => {
        if (timerRef.current) { clearTimeout(timerRef.current); }
        timerRef.current = setTimeout(() => { timerRef.current = null; flush(); }, DEBOUNCE_MS);
    }, [flush]);

    const refresh = useCallback(async () => {
        if (!projectId) { setIsReady(true); return; }
        try {
            const res = await api.get(endpoints.surveys(projectId));
            const server = res?.clusters || {};
            const local = dataRef.current || {};
            const merged = {};
            const clusterIds = new Set([...Object.keys(local), ...Object.keys(server)]);
            for (const c of clusterIds) {
                const sectionIds = new Set([...Object.keys(local[c] || {}), ...Object.keys(server[c] || {})]);
                for (const s of sectionIds) {
                    const key = keyOf(c, s);
                    const localSec = local[c]?.[s];
                    const serverSec = server[c]?.[s];
                    let chosen;
                    if (dirtyRef.current.has(key) && localSec) {
                        // Lokale invoer wint, maar upload-status komt altijd van de server.
                        chosen = { ...localSec };
                        for (const item of specSection(c, s)?.items || []) {
                            if (item.type !== 'photo' && item.type !== 'file') { continue; }
                            if (serverSec && serverSec[item.id] != null) { chosen[item.id] = serverSec[item.id]; }
                            else { delete chosen[item.id]; }
                        }
                    } else if (serverSec) {
                        chosen = serverSec;
                    } else {
                        // Alleen lokaal bekend: bewaren als er echte invoer in zit
                        // (dan is hij nooit aangekomen) en opnieuw aanbieden.
                        const items = sanitize(c, s, localSec);
                        const hasInput = items === null
                            || Object.values(items).some(v => present(v) && !(Array.isArray(v) && v.length === 0));
                        if (!hasInput) { continue; }
                        chosen = localSec;
                        dirtyRef.current.add(key);
                    }
                    (merged[c] ||= {})[s] = chosen;
                }
            }
            dataRef.current = merged;
            setData(merged);
            persistDirty();
            if (dirtyRef.current.size > 0) { scheduleFlush(); }
        } catch (err) {
            // Offline of serverfout: lokale gegevens blijven leidend.
            console.warn('[useSurvey] laden mislukt:', err?.message);
        } finally {
            setIsReady(true);
        }
    }, [projectId, setData, sanitize, persistDirty, scheduleFlush]);

    // Laden zodra project én cluster-config bekend zijn.
    const hasConfig = Boolean(clustersConfig?.clusters?.length);
    useEffect(() => {
        dirtyRef.current = readDirty(dirtyKey);
        versionsRef.current = new Map();
        setPendingCount(dirtyRef.current.size);
        setIsReady(false);
        if (hasConfig) { refresh(); }
    }, [dirtyKey, hasConfig, refresh]);

    // Retry zolang er iets openstaat; direct proberen zodra de verbinding terug is.
    useEffect(() => {
        if (!projectId) { return undefined; }
        const tick = () => { if (dirtyRef.current.size > 0 && !timerRef.current) { flush(); } };
        const interval = setInterval(tick, RETRY_INTERVAL_MS);
        const onHide = () => { if (document.visibilityState === 'hidden') { flush(); } };
        window.addEventListener('online', tick);
        document.addEventListener('visibilitychange', onHide);
        return () => {
            clearInterval(interval);
            window.removeEventListener('online', tick);
            document.removeEventListener('visibilitychange', onHide);
        };
    }, [projectId, flush]);

    const setSection = useCallback((clusterId, sectionId, patch) => {
        const key = keyOf(clusterId, sectionId);
        const prev = dataRef.current || {};
        const next = {
            ...prev,
            [clusterId]: {
                ...(prev[clusterId] || {}),
                [sectionId]: { ...(prev[clusterId]?.[sectionId] || {}), ...patch },
            },
        };
        dataRef.current = next;
        setData(next);
        versionsRef.current.set(key, (versionsRef.current.get(key) || 0) + 1);
        dirtyRef.current.add(key);
        persistDirty();
        scheduleFlush();
    }, [setData, persistDirty, scheduleFlush]);

    const setItem = useCallback((clusterId, sectionId, itemId, value) => {
        setSection(clusterId, sectionId, { [itemId]: value });
    }, [setSection]);

    const getSection = useCallback((clusterId, sectionId) => {
        return data?.[clusterId]?.[sectionId] || {};
    }, [data]);

    const getItem = useCallback((clusterId, sectionId, itemId) => {
        const v = data?.[clusterId]?.[sectionId]?.[itemId];
        if (v !== undefined && v !== null) { return v; }
        const item = specSection(clusterId, sectionId)?.items?.find(i => i.id === itemId);
        return defaultForType(item);
    }, [data]);

    // Openstaande wijzigingen meteen versturen bij verlaten van de wizard.
    useEffect(() => () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        flush();
    }, [flush]);

    const progress = useMemo(() => {
        const out = {};
        for (const cluster of (clustersConfig?.clusters || [])) {
            const variant = preferred?.[cluster.id] || 1;
            out[cluster.id] = clusterProgress(cluster, data?.[cluster.id], variant);
            out[cluster.id].complete = clusterComplete(cluster, data?.[cluster.id], variant);
        }
        return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clustersConfig, data, JSON.stringify(preferred)]);

    return {
        data,
        setItem,
        setSection,
        getItem,
        getSection,
        syncState,
        lastError,
        pendingCount,
        refresh,
        flush,
        dropVariant,
        isReady,
        progress,
    };
}
